import { CONFIG } from "../config.js";
import { googleSheetsService } from "../services/googleSheets.js";
import {
  validateRequired,
  validateAmount,
  validateDate,
} from "../utils/validators.js";

/**
 * Transaction Form Component
 * Form for recording income and expenses in Liquid Glass Style
 */
export class TransactionForm {
  constructor() {
    this.metadata = null;
    this.isSubmitting = false;
  }

  /**
   * Load metadata for form dropdowns
   */
  async loadMetadata() {
    try {
      this.metadata = await googleSheetsService.getMetadata();
    } catch (error) {
      console.error("Error loading metadata:", error);
      this.showError("ບໍ່ສາມາດໂຫຼດຂໍ້ມູນຕັ້ງຄ່າໄດ້");
    }
  }

  /**
   * Render transaction form
   */
  render() {
    if (!this.metadata) {
      return `
        <div class="loading-overlay">
          <div class="liquid-spinner"></div>
          <div class="loading-text">ກຳລັງໂຫຼດຟອມ...</div>
        </div>
      `;
    }

    const today = new Date().toISOString().split("T")[0];

    return `
      <div class="page-container">
        <div class="page-header">
          <h1>📝 ບັນທຶກລາຍຮັບລາຍຈ່າຍ</h1>
          <p class="page-subtitle">ບັນທຶກລາຍການລາຍຮັບ ແລະ ລາຍຈ່າຍຂອງທ່ານດ້ວຍລະບົບທີ່ທັນສະໄໝ</p>
        </div>

        <div class="glass-panel">
          <form id="transactionForm" class="transaction-form" autocomplete="off">
            <div class="form-grid-3">
              <div class="form-group">
                <label for="transactionDate">ວັນທີ *</label>
                <input type="date" id="transactionDate" name="date" value="${today}" required>
              </div>

              <div class="form-group">
                <label for="transactionType">ປະເພດ *</label>
                <select id="transactionType" name="type" required>
                  <option value="">ເລືອກປະເພດ</option>
                  <option value="ລາຍຮັບ">ລາຍຮັບ</option>
                  <option value="ລາຍຈ່າຍ">ລາຍຈ່າຍ</option>
                </select>
              </div>

              <div class="form-group">
                <label for="transactionAmount">ຈຳນວນເງິນ *</label>
                <input type="number" id="transactionAmount" name="amount" step="0.01" placeholder="0.00" required>
              </div>

              <div class="form-group">
                <label for="transactionCurrency">ສະກຸນເງິນ *</label>
                <select id="transactionCurrency" name="currency" required>
                  ${this.renderOptions(this.metadata.currencies, "symbol")}
                </select>
              </div>

              <div class="form-group" id="exchangeRateGroup" style="display: none;">
                <label for="transactionExchangeRate" id="exchangeRateLabel">ອັດຕາແລກປ່ຽນ (1 ? = ? ₭) *</label>
                <input type="number" id="transactionExchangeRate" name="exchangeRate" step="any" min="0" placeholder="0.00">
              </div>

              <div class="form-group">
                <label for="transactionCategory">ໝວດໝູ່ *</label>
                <select id="transactionCategory" name="category" required>
                  <option value="">ເລືອກໝວດໝູ່</option>
                  ${this.renderOptions(this.metadata.categories, "name")}
                </select>
              </div>

              <div class="form-group">
                <label for="transactionProject">ໂຄງການ</label>
                <select id="transactionProject" name="project">
                  <option value="">ບໍ່ລະບຸໂຄງການ</option>
                  ${this.renderOptions(this.metadata.projects)}
                </select>
              </div>

              <div class="form-group">
                <label for="transactionAccountType">ປະເພດບັນຊີ *</label>
                <select id="transactionAccountType" name="accountType" required>
                  <option value="">ເລືອກປະເພດບັນຊີ</option>
                  ${this.renderOptions(this.metadata.accountTypes)}
                </select>
              </div>

              <div class="form-group">
                <label for="transactionItem">ລາຍການ *</label>
                <input type="text" id="transactionItem" name="item" placeholder="ຊື່ລາຍການ" required>
              </div>
            </div>

            <div class="form-group">
              <label for="transactionDescription">ລາຍລະອຽດ</label>
              <textarea id="transactionDescription" name="description" rows="2" placeholder="ລາຍລະອຽດເພີ່ມເຕີມ..."></textarea>
            </div>

            <div class="form-group">
              <label for="transactionAttachment">ເອກະສານແນບ</label>
              <input type="file" id="transactionAttachment" name="attachment" accept="image/*,.pdf">
              <small class="form-hint">ອັບໂຫຼດຮູບພາບ ຫຼື PDF (ສູງສຸດ 10MB)</small>
            </div>

            <div class="form-actions">
              <button type="button" class="btn btn-secondary" id="resetBtn">ລ້າງຟອມ</button>
              <button type="submit" class="btn btn-primary" id="submitBtn">
                <span class="btn-text">🚀 ບັນທຶກຂໍ້ມູນ</span>
                <span class="btn-loader" style="display: none;">ກຳລັງບັນທຶກ...</span>
              </button>
            </div>
          </form>

          <div id="formMessage" class="form-message" style="display: none;"></div>
        </div>
      </div>
    `;
  }

  /**
   * Render select options
   */
  renderOptions(items, property = null) {
    if (!items || items.length === 0) return "";
    return items
      .map((item) => {
        const value = property ? item[property] : item;
        const label = property ? item.name || item.symbol || item : item;
        return `<option value="${value}">${label}</option>`;
      })
      .join("");
  }

  /**
   * Inject loading overlay and success dialog into the DOM (once)
   */
  injectDialogs() {
    if (document.getElementById("submitLoadingOverlay")) return;

    // Loading overlay
    const loadingEl = document.createElement("div");
    loadingEl.id = "submitLoadingOverlay";
    loadingEl.className = "submit-loading-overlay";
    loadingEl.innerHTML = `
      <div class="submit-loading-spinner"></div>
      <div class="submit-loading-text">ກຳລັງບັນທຶກຂໍ້ມູນ...</div>
    `;
    document.body.appendChild(loadingEl);

    // Success dialog
    const successEl = document.createElement("div");
    successEl.id = "submitSuccessOverlay";
    successEl.className = "success-dialog-overlay";
    successEl.innerHTML = `
      <div class="success-dialog">
        <div class="success-dialog-icon">✅</div>
        <div class="success-dialog-title">ບັນທຶກສຳເລັດ!</div>
        <div class="success-dialog-message">ລາຍການຖືກບັນທຶກລົງໃນລະບົບແລ້ວ</div>
        <button class="success-dialog-btn" id="successDialogOkBtn">ຕົກລົງ</button>
      </div>
    `;
    document.body.appendChild(successEl);

    // Close on OK button
    document
      .getElementById("successDialogOkBtn")
      .addEventListener("click", () => {
        this.hideSuccessDialog();
      });
  }

  /** Show the loading overlay */
  showLoadingOverlay(text = "ກຳລັງບັນທຶກຂໍ້ມູນ...") {
    const el = document.getElementById("submitLoadingOverlay");
    if (!el) return;
    const textEl = el.querySelector(".submit-loading-text");
    if (textEl) textEl.textContent = text;
    el.classList.add("active");
  }

  /** Hide the loading overlay */
  hideLoadingOverlay() {
    const el = document.getElementById("submitLoadingOverlay");
    if (el) el.classList.remove("active");
  }

  /** Show the success dialog, auto-dismiss after 2.5 s */
  showSuccessDialog() {
    const el = document.getElementById("submitSuccessOverlay");
    if (!el) return;
    el.classList.add("active");
    // Auto-dismiss after 2.5 seconds
    this._successTimer = setTimeout(() => this.hideSuccessDialog(), 2500);
  }

  /** Hide the success dialog */
  hideSuccessDialog() {
    clearTimeout(this._successTimer);
    const el = document.getElementById("submitSuccessOverlay");
    if (el) el.classList.remove("active");
  }

  /**
   * Initialize form after rendering
   */
  initialize() {
    this.injectDialogs();

    const form = document.getElementById("transactionForm");
    if (form) {
      form.addEventListener("submit", (e) => this.handleSubmit(e));
    }

    const resetBtn = document.getElementById("resetBtn");
    if (resetBtn) {
      resetBtn.addEventListener("click", () => this.resetForm());
    }

    // Filter categories on type change
    const typeSelect = document.getElementById("transactionType");
    if (typeSelect) {
      typeSelect.addEventListener("change", (e) =>
        this.filterCategoriesByType(e.target.value),
      );
    }

    // Auto-select first currency if available
    const currencySelect = document.getElementById("transactionCurrency");
    if (currencySelect && this.metadata.currencies.length > 0) {
      currencySelect.value = this.metadata.currencies[0].symbol;
      this.handleCurrencyChange(currencySelect.value);
    }

    // Show/hide exchange rate field on currency change
    if (currencySelect) {
      currencySelect.addEventListener("change", (e) =>
        this.handleCurrencyChange(e.target.value),
      );
    }
  }

  /**
   * Show or hide the exchange rate field based on selected currency
   */
  handleCurrencyChange(currencySymbol) {
    const group = document.getElementById("exchangeRateGroup");
    const input = document.getElementById("transactionExchangeRate");
    const label = document.getElementById("exchangeRateLabel");
    if (!group || !input) return;

    const isForeign = currencySymbol && currencySymbol !== "₭";
    group.style.display = isForeign ? "" : "none";
    input.required = isForeign;
    if (!isForeign) input.value = "";
    if (label && isForeign) {
      label.textContent = `ອັດຕາແລກປ່ຽນ (1 ${currencySymbol} = ? ₭) *`;
    }
  }

  /**
   * Filter categories based on transaction type
   */
  filterCategoriesByType(type) {
    const categorySelect = document.getElementById("transactionCategory");
    if (!categorySelect || !this.metadata || !this.metadata.categories) return;

    let filteredCategories = this.metadata.categories;

    if (type) {
      filteredCategories = this.metadata.categories.filter(
        (cat) => cat.type === type,
      );
    }

    const options = this.renderOptions(filteredCategories, "name");
    categorySelect.innerHTML = `<option value="">ເລືອກໝວດໝູ່</option>${options}`;
  }

  /**
   * Handle form submission
   */
  async handleSubmit(e) {
    e.preventDefault();
    if (this.isSubmitting) return;

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Basic validation
    if (
      !data.date ||
      !data.type ||
      !data.amount ||
      !data.category ||
      !data.item
    ) {
      this.showError("ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບທ້ວນ");
      return;
    }

    // Validate exchange rate for foreign currencies
    const isForeignCurrency = data.currency && data.currency !== "₭";
    if (
      isForeignCurrency &&
      (!data.exchangeRate || parseFloat(data.exchangeRate) <= 0)
    ) {
      this.showError("ກະລຸນາປ້ອນອັດຕາແລກປ່ຽນທີ່ຖືກຕ້ອງ");
      return;
    }

    // Map description field → moreInfo (saved in column K: MoreInfo)
    data.moreInfo = data.description || "";

    // Extract attachment file (if any)
    const attachmentInput = document.getElementById("transactionAttachment");
    const attachmentFile =
      attachmentInput && attachmentInput.files.length > 0
        ? attachmentInput.files[0]
        : null;

    this.isSubmitting = true;
    this.setSubmitting(true);

    // Show loading overlay
    const hasFile = !!attachmentFile;
    this.showLoadingOverlay(
      hasFile ? "ກຳລັງອັບໂຫຼດໄຟລ໌ ແລະ ບັນທຶກຂໍ້ມູນ..." : "ກຳລັງບັນທຶກຂໍ້ມູນ...",
    );

    try {
      await googleSheetsService.submitTransaction(data, attachmentFile);
      this.hideLoadingOverlay();
      this.showSuccessDialog();
      this.resetForm();
    } catch (error) {
      console.error("Error submitting:", error);
      this.hideLoadingOverlay();
      this.showError("❌ ເກີດຄວາມຜິດພາດ: " + error.message);
    } finally {
      this.isSubmitting = false;
      this.setSubmitting(false);
    }
  }

  setSubmitting(isSubmitting) {
    const submitBtn = document.getElementById("submitBtn");
    if (submitBtn) {
      submitBtn.disabled = isSubmitting;
      const text = submitBtn.querySelector(".btn-text");
      const loader = submitBtn.querySelector(".btn-loader");
      if (text && loader) {
        text.style.display = isSubmitting ? "none" : "inline";
        loader.style.display = isSubmitting ? "inline" : "none";
      }
    }
  }

  resetForm() {
    const form = document.getElementById("transactionForm");
    if (form) form.reset();
    this.filterCategoriesByType(""); // Reset categories to show all
    this.hideMessage();
  }

  showSuccess(message) {
    const messageEl = document.getElementById("formMessage");
    if (messageEl) {
      messageEl.textContent = message;
      messageEl.className = "form-message success";
      messageEl.style.display = "block";
      messageEl.scrollIntoView({ behavior: "smooth" });
    }
  }

  showError(message) {
    const messageEl = document.getElementById("formMessage");
    if (messageEl) {
      messageEl.textContent = message;
      messageEl.className = "form-message error";
      messageEl.style.display = "block";
    }
  }

  hideMessage() {
    const messageEl = document.getElementById("formMessage");
    if (messageEl) messageEl.style.display = "none";
  }
}
